import {CODE_PERMISSIONS, type CodePermissionKey} from '@common/permissions';

// NOTE: keys MUST stay in sync with the `UserNotificationType` Prisma enum
// (apps/api/prismas/prisma/models/user.prisma). Adding an event = add an enum
// value (human-run migration) + an entry here.
export type UserNotificationType =
  | 'RFC_SUBMISSION_RECEIVED'
  | 'RFC_STATUS_CHANGED'
  | 'PAYROLL_UPLOAD_PROCESSED'
  | 'SYSTEM_MAINTENANCE'
  | 'SECURITY_ALERT'
  | 'BROKER_NOTE_ADDED'
  | 'BROKER_DEAL_APPROVED'
  | 'TICKET_SUBMITTED'
  | 'TICKET_CBS_REVIEW';

export type NotificationCategory = 'RFC' | 'PAYROLL' | 'DEALS' | 'TICKETS' | 'SECURITY' | 'SYSTEM';

export type NotificationAudience = 'clientAdmin' | 'groupAdmin' | 'broker' | 'internal';

export type NotificationChannel = 'email' | 'inApp';

export type NotificationPriority = 'critical' | 'high' | 'medium' | 'info';

export type NotificationEventDef = {
  key: UserNotificationType;
  category: NotificationCategory;
  label: string;
  description: string;
  priority: NotificationPriority;
  /** false => "Always on": cannot be toggled off (enforced in UI and server). */
  mutable: boolean;
  channels: NotificationChannel[];
  audiences: NotificationAudience[];
  /**
   * Channel state for users with no stored setting row. Defaults to `true`;
   * `false` makes the event opt-in (nobody receives it until they enable it).
   */
  defaultEnabled?: boolean;
  /**
   * Permission the caller must hold (in addition to matching an audience) to
   * see, toggle, or receive the event.
   */
  requiredPermission?: CodePermissionKey;
};

export const NOTIFICATION_CATEGORY_LABELS: Record<NotificationCategory, string> = {
  RFC: 'Requests for Coverage',
  PAYROLL: 'Payroll',
  DEALS: 'Deals & Brokers',
  TICKETS: 'Tickets',
  SECURITY: 'Security',
  SYSTEM: 'System',
};

export const NOTIFICATION_REGISTRY: Record<UserNotificationType, NotificationEventDef> = {
  RFC_SUBMISSION_RECEIVED: {
    key: 'RFC_SUBMISSION_RECEIVED',
    category: 'RFC',
    label: 'RFC Submission Notifications',
    description: 'Notifications when a new Request for Coverage (RFC) is submitted.',
    priority: 'high',
    mutable: true,
    channels: ['email'],
    audiences: ['clientAdmin', 'internal'],
  },
  RFC_STATUS_CHANGED: {
    key: 'RFC_STATUS_CHANGED',
    category: 'RFC',
    label: 'RFC Status Change Notifications',
    description:
      'Notifications when the status of an RFC changes (approved, rejected, completed).',
    priority: 'medium',
    mutable: true,
    channels: ['email'],
    audiences: ['internal'],
  },
  PAYROLL_UPLOAD_PROCESSED: {
    key: 'PAYROLL_UPLOAD_PROCESSED',
    category: 'PAYROLL',
    label: 'Successful Payroll Upload Notifications',
    description: 'Notifications when a group payroll upload has been processed successfully.',
    priority: 'medium',
    mutable: true,
    channels: ['email'],
    audiences: ['groupAdmin'],
  },
  SYSTEM_MAINTENANCE: {
    key: 'SYSTEM_MAINTENANCE',
    category: 'SYSTEM',
    label: 'System Maintenance Notifications',
    description: 'Notifications about upcoming system maintenance and downtime.',
    priority: 'info',
    mutable: true,
    channels: ['email'],
    audiences: ['clientAdmin', 'groupAdmin', 'broker', 'internal'],
  },
  SECURITY_ALERT: {
    key: 'SECURITY_ALERT',
    category: 'SECURITY',
    label: 'Security Alert Notifications',
    description: 'Notifications about important security alerts related to your account.',
    priority: 'critical',
    mutable: false,
    channels: ['email'],
    audiences: ['clientAdmin', 'groupAdmin', 'broker', 'internal'],
  },
  BROKER_NOTE_ADDED: {
    key: 'BROKER_NOTE_ADDED',
    category: 'DEALS',
    label: 'Note Added to Your Deals',
    description: 'Notifications when a note is added to a prospect or deal assigned to the broker.',
    priority: 'medium',
    mutable: true,
    channels: ['email', 'inApp'],
    audiences: ['broker'],
  },
  BROKER_DEAL_APPROVED: {
    key: 'BROKER_DEAL_APPROVED',
    category: 'DEALS',
    label: 'Deal Approved by Client',
    description: 'Notifications when a client admin approves a deal assigned to the broker.',
    priority: 'high',
    mutable: true,
    channels: ['email', 'inApp'],
    audiences: ['broker'],
  },
  TICKET_SUBMITTED: {
    key: 'TICKET_SUBMITTED',
    category: 'TICKETS',
    label: 'New Ticket Submitted',
    description: 'Notifications when a new support ticket is submitted in your portal.',
    priority: 'high',
    mutable: true,
    channels: ['email', 'inApp'],
    audiences: ['clientAdmin'],
    defaultEnabled: false,
    requiredPermission: CODE_PERMISSIONS.tickets.submissionAlerts.key,
  },
  TICKET_CBS_REVIEW: {
    key: 'TICKET_CBS_REVIEW',
    category: 'TICKETS',
    label: 'Ticket Awaiting Clever Benefits Review',
    description:
      'Emails when a portal ticket reaches Clever Benefits and is awaiting review on the CBS ticket queue.',
    priority: 'high',
    mutable: true,
    channels: ['email'],
    audiences: ['internal'],
    defaultEnabled: false,
    requiredPermission: CODE_PERMISSIONS.tickets.cbsSubmissionAlerts.key,
  },
};

export function getEventDef(type: UserNotificationType): NotificationEventDef {
  return NOTIFICATION_REGISTRY[type];
}

export function isMutable(type: UserNotificationType): boolean {
  return NOTIFICATION_REGISTRY[type].mutable;
}

export function getEventsForAudience(audience: NotificationAudience): NotificationEventDef[] {
  return Object.values(NOTIFICATION_REGISTRY).filter((e) => e.audiences.includes(audience));
}

export function groupByCategory(
  events: NotificationEventDef[],
): Array<{category: NotificationCategory; label: string; events: NotificationEventDef[]}> {
  const order = Object.keys(NOTIFICATION_CATEGORY_LABELS) as NotificationCategory[];
  return order
    .map((category) => ({
      category,
      label: NOTIFICATION_CATEGORY_LABELS[category],
      events: events.filter((e) => e.category === category),
    }))
    .filter((g) => g.events.length > 0);
}
